import { useEffect, useRef, type RefObject } from 'react';

/*
 * Anchored placement for popovers. Menu and Tooltip both hang a top-layer element off a
 * trigger, and both have to answer the same three questions: which side does it go on,
 * what happens when that side doesn't fit, and how does it stay attached while the page
 * scrolls. Written once here rather than twice there.
 *
 * Not CSS anchor positioning: no Firefox, and Menu already shipped on measurement.
 */

export type Side = 'top' | 'right' | 'bottom' | 'left';
export type Align = 'start' | 'center' | 'end';

type Size = { width: number; height: number };

type Placement = {
  side: Side;
  align: Align;
  /** Between the trigger and the element. */
  gap: number;
  /** The least the element may come to the viewport edge before it's pulled back in. */
  margin: number;
  viewport: Size;
};

const OPPOSITE: Record<Side, Side> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

const isHorizontal = (side: Side) => side === 'left' || side === 'right';

/** Where the element starts on the axis it's offset along. */
const mainOffset = (side: Side, anchor: DOMRect, box: Size, gap: number) => {
  if (side === 'top') return anchor.top - box.height - gap;
  if (side === 'bottom') return anchor.bottom + gap;
  if (side === 'left') return anchor.left - box.width - gap;
  return anchor.right + gap;
};

const fitsOn = (side: Side, anchor: DOMRect, box: Size, placement: Placement) => {
  const start = mainOffset(side, anchor, box, placement.gap);
  const extent = isHorizontal(side) ? box.width : box.height;
  const limit = isHorizontal(side) ? placement.viewport.width : placement.viewport.height;
  return start >= placement.margin && start + extent <= limit - placement.margin;
};

/** Where the element sits on the axis it's aligned along. */
const crossOffset = (side: Side, align: Align, anchor: DOMRect, box: Size) => {
  const horizontal = isHorizontal(side);
  const start = horizontal ? anchor.top : anchor.left;
  const anchorExtent = horizontal ? anchor.height : anchor.width;
  const boxExtent = horizontal ? box.height : box.width;

  if (align === 'start') return start;
  if (align === 'end') return start + anchorExtent - boxExtent;
  return start + anchorExtent / 2 - boxExtent / 2;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Viewport coordinates for an element anchored to a trigger, plus the side it actually
 * landed on — which is not always the side asked for, and is what an arrow needs to know
 * which way to point.
 *
 * Pure, so the flipping and clamping are testable without a layout engine.
 */
export const place = (anchor: DOMRect, box: Size, placement: Placement) => {
  /* Flip to the opposite side when the requested one doesn't fit — a menu on the last
     row of a table, a tooltip on the top row. If neither fits, keep the requested side
     and let the clamp below deal with it; flip-flopping between two bad options is
     worse than one predictable one. */
  const side = fitsOn(placement.side, anchor, box, placement)
    ? placement.side
    : fitsOn(OPPOSITE[placement.side], anchor, box, placement)
      ? OPPOSITE[placement.side]
      : placement.side;

  const horizontal = isHorizontal(side);
  const crossExtent = horizontal ? box.height : box.width;
  const crossLimit = horizontal ? placement.viewport.height : placement.viewport.width;

  const main = Math.max(mainOffset(side, anchor, box, placement.gap), placement.margin);
  const cross = clamp(
    crossOffset(side, placement.align, anchor, box),
    placement.margin,
    Math.max(crossLimit - crossExtent - placement.margin, placement.margin),
  );

  return horizontal ? { top: cross, left: main, side } : { top: main, left: cross, side };
};

type Options = {
  elementRef: RefObject<HTMLElement | null>;
  /** Read fresh each time: the trigger may not exist yet, or may have moved. */
  getAnchor: () => Element | null;
  side: Side;
  align?: Align;
  gap?: number;
  margin?: number;
};

/**
 * Keeps `elementRef` positioned against its trigger for as long as it's open, and stamps
 * the side it landed on as `data-side` so CSS can point an arrow without a re-render.
 */
export const useAnchoredPosition = ({
  elementRef,
  getAnchor,
  side,
  align = 'center',
  gap = 4,
  margin = 8,
}: Options) => {
  /* The caller rebuilds getAnchor every render. Kept in a ref so the listeners below are
     attached once instead of being torn down and re-attached each time, while still
     calling the current closure rather than the one from mount. */
  const anchorGetter = useRef(getAnchor);
  anchorGetter.current = getAnchor;

  useEffect(() => {
    const element = elementRef.current;
    if (element === null) return;

    /* Declared ahead of `update` so it can reach them, assigned after — `update` is what
       decides which anchor to watch, and the anchor may not exist until it first runs. */
    let observer: ResizeObserver | undefined;
    let observed: Element | null = null;

    const update = () => {
      // A closed popover has no box worth measuring.
      if (!element.matches(':popover-open')) return;

      const anchor = anchorGetter.current();
      if (anchor === null) return;

      /* Both boxes can change size while the element is open, and neither shows up as a
         scroll or a resize: a field grows to make room for its clear button, and a list
         waiting on async results changes height when they land — which matters most when
         it has flipped upward and its top depends on that height. */
      if (observer !== undefined && anchor !== observed) {
        if (observed !== null) observer.unobserve(observed);
        observer.observe(anchor);
        observed = anchor;
      }

      const rect = anchor.getBoundingClientRect();

      /* Published before the element is measured, not after. A list that sizes itself
         from this would otherwise be measured at its unset width — for a fixed element
         that means the whole viewport — and the clamp below would decide it can't fit
         and pin it to the margin. Reading the rect on the next line flushes it. */
      element.style.setProperty('--anchor-width', `${rect.width}px`);

      const result = place(rect, element.getBoundingClientRect(), {
        side,
        align,
        gap,
        margin,
        viewport: { width: window.innerWidth, height: window.innerHeight },
      });

      element.style.top = `${result.top}px`;
      element.style.left = `${result.left}px`;
      element.dataset.side = result.side;

      /* Where the trigger's centre falls inside the element, so an arrow can point at it
         rather than at the element's own middle. Those differ whenever the clamp above
         has pulled the element back from an edge — which is exactly when a centred arrow
         detaches from the thing it's meant to be pointing at. */
      const centre = isHorizontal(result.side)
        ? rect.top + rect.height / 2 - result.top
        : rect.left + rect.width / 2 - result.left;
      element.style.setProperty('--anchor-centre', `${centre}px`);
    };

    // jsdom has no ResizeObserver, and apps consume this library from source.
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(update);
      observer.observe(element);
    }

    /* Re-placed while open, not just on open: measuring once leaves the element behind
       the moment the page scrolls. Capture phase catches scrolling ancestors, not only
       the window. */
    element.addEventListener('toggle', update);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      element.removeEventListener('toggle', update);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [elementRef, side, align, gap, margin]);
};
